import { Button, formatDate, useTranslation } from '@hbs-us/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { useSession } from '../../auth/useSession.js';
import { ApiError, apiFetchJson } from '../../lib/apiFetch.js';
import { canIssueLoginCode } from './PhoneSignInCode.js';

/**
 * "Signed-in devices" on the employee profile: every phone or browser this person is signed in on,
 * newest use first, each with a way to sign it out. A signed-out device cannot renew and stops
 * within 15 minutes. The server decides again who may see and act; hiding the card is UX only.
 *
 * It uses the same rule as the phone sign-in code beside it: whoever may put someone on a phone may
 * take them off one.
 */

/** Rows per page. The earlier sign-ins share one row, which counts as one. */
export const DEVICES_PAGE_SIZE = 5;

export interface SignedInDevice {
  id: string;
  client: string | null;
  label: string | null;
  signedInAt: string;
  lastUsedAt: string | null;
}

interface DevicesResponse {
  ok: true;
  devices: SignedInDevice[];
}

/** The device's own name, else a name for its kind, else "Earlier sign-in" (made before names). */
export function deviceName(
  device: Pick<SignedInDevice, 'client' | 'label'>,
  t: (key: string) => string,
): string {
  if (device.label) return device.label;
  if (device.client === 'ios' || device.client === 'android' || device.client === 'web') {
    return t(`employees:devices.kind.${device.client}`);
  }
  return t('employees:devices.kind.earlier');
}

/** A missing route answers with the HTML not-found page, so it has no error code of its own. */
function isMissingRoute(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 404 && error.message === 'request_failed_404'
  );
}

export function SignedInDevices({ employeeId }: { employeeId: number }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const access = useAccess();
  const { session } = useSession();
  const allowed = canIssueLoginCode(
    access({ minRole: 'Admin' }),
    access({ minRole: 'Manager' }),
    session?.companySettings?.managersIssueLoginCodes,
  );
  const [confirming, setConfirming] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();

  const path = `/api/v1/employees/${employeeId}/devices`;
  const devices = useQuery<DevicesResponse>({
    queryKey: ['employee-devices', employeeId],
    queryFn: () => apiFetchJson<DevicesResponse>(path),
    enabled: allowed,
    retry: false,
  });
  // 'earlier' ends every sign-in made before devices had names; anything else is one device's id.
  const signOut = useMutation<unknown, Error, string>({
    mutationFn: (deviceId) =>
      apiFetchJson(`${path}/${encodeURIComponent(deviceId)}/sign-out`, { method: 'POST' }),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ['employee-devices', employeeId] });
    },
  });

  // No login yet means nothing to sign out of, and an old server means no feature: no card.
  const noLogin = devices.error instanceof ApiError && devices.error.message === 'no_login';
  if (!allowed || noLogin || isMissingRoute(devices.error)) return null;

  const when = (iso: string) =>
    formatDate(iso, locale, { dateStyle: 'medium', timeStyle: 'short' });
  const error = devices.error ?? signOut.error;
  const all = devices.data?.devices ?? [];
  const named = all.filter((device) => device.client !== null);
  // Sign-ins made before devices had names cannot be told apart, so they share one row.
  const earlier = all.filter((device) => device.client === null);
  const latestEarlierUse = earlier.reduce<string | null>((latest, device) => {
    const used = device.lastUsedAt ?? device.signedInAt;
    return latest && latest > used ? latest : used;
  }, null);

  // One list to page through: each named device, then the earlier sign-ins as one row.
  const rows = named.map((device) => ({
    key: device.id,
    name: deviceName(device, t),
    detail: t('employees:devices.lastUsed', { when: when(device.lastUsedAt ?? device.signedInAt) }),
    confirmText: t('employees:devices.confirm'),
    actionText: t('employees:devices.signOut'),
  }));
  if (earlier.length > 0 && latestEarlierUse) {
    rows.push({
      key: 'earlier',
      name: t('employees:devices.earlier.name', { count: earlier.length }),
      detail: t('employees:devices.lastUsed', { when: when(latestEarlierUse) }),
      confirmText: t('employees:devices.earlier.confirm', { count: earlier.length }),
      actionText: t('employees:devices.earlier.signOut'),
    });
  }
  const pages = Math.max(1, Math.ceil(rows.length / DEVICES_PAGE_SIZE));
  // A sign-out can empty the last page; show the page before it rather than an empty card.
  const current = Math.min(page, pages - 1);
  const first = current * DEVICES_PAGE_SIZE;

  return (
    <section className="rounded-md border border-border bg-card p-6 md:col-span-2">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('employees:devices.heading')}
      </h2>
      <p className="mb-4 max-w-prose text-muted-foreground text-sm">
        {t('employees:devices.description')}
      </p>

      {devices.isPending ? null : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('employees:devices.none')}</p>
      ) : (
        <>
          <ul className="divide-y divide-border">
            {rows.slice(first, first + DEVICES_PAGE_SIZE).map((row) => (
              <DeviceRow
                key={row.key}
                name={row.name}
                detail={row.detail}
                confirmText={row.confirmText}
                actionText={row.actionText}
                cancelText={t('employees:devices.cancel')}
                confirming={confirming === row.key}
                pending={signOut.isPending}
                onAsk={() => setConfirming(row.key)}
                onCancel={() => setConfirming(null)}
                onConfirm={() => signOut.mutate(row.key)}
              />
            ))}
          </ul>
          {pages > 1 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-muted-foreground text-sm tabular-nums">
                {t('employees:devices.range', {
                  from: first + 1,
                  to: Math.min(first + DEVICES_PAGE_SIZE, rows.length),
                  total: rows.length,
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={current === 0}
                  onClick={() => setPage(current - 1)}
                >
                  {t('employees:devices.previous')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={current >= pages - 1}
                  onClick={() => setPage(current + 1)}
                >
                  {t('employees:devices.next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('employees:devices.error')}
        </p>
      ) : null}
    </section>
  );
}

function DeviceRow(props: {
  name: string;
  detail: string;
  confirmText: string;
  actionText: string;
  cancelText: string;
  confirming: boolean;
  pending: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground">{props.name}</p>
        <p className="text-muted-foreground text-sm">{props.detail}</p>
      </div>
      {props.confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm">{props.confirmText}</span>
          <Button
            type="button"
            variant="destructive"
            disabled={props.pending}
            onClick={props.onConfirm}
          >
            {props.actionText}
          </Button>
          <Button type="button" variant="ghost" onClick={props.onCancel}>
            {props.cancelText}
          </Button>
        </div>
      ) : (
        <Button type="button" variant="secondary" onClick={props.onAsk}>
          {props.actionText}
        </Button>
      )}
    </li>
  );
}
